import React, { useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Icon, IconProps } from "semantic-ui-react";

import { PurClientConfig, PurRecord } from "react-lib/apis/PUR/purApi";
import { PurDocumentVerificationAccess } from "react-lib/apis/PUR/purDocumentVerification";
import { PurReceiptWriteAccess } from "react-lib/apis/PUR/purReceiptCreate";

import MeERPScreenScaffold, { MeERPScreenId } from "./MeERPScreenScaffold";
import MeERPOverview from "./MeERPOverview";
import PurResourceList from "./PurResourceList";
import PurDocuments from "./PurDocuments";
import MeERPPhaseB, { MeERPPhaseBHandle } from "./MeERPPhaseB";
import { Event as MeERPEvent, MeERPSlice } from "./MeERPInterface";
import { usePurVerificationStore } from "./purVerificationState";

import "./CardMeERP.scss";

interface CardMeERPProps {
  selectedDivision?: { id?: number | string; name?: string } | null;
  apiBaseUrl: string;
  apiToken?: string;
  purPathPrefix?: string;
  onEvent: (event: MeERPEvent) => void;
  MeERP: MeERPSlice;
  documentVerificationAccess?: PurDocumentVerificationAccess;
  receiptWriteAccess?: PurReceiptWriteAccess;
}

type ProcurementTab = "materialRequests" | "purchaseOrders";
type ReceivingTab = "pendingOrders" | "receipts";

const SCREEN_GROUPS: Array<{ label: string; screens: MeERPScreenId[] }> = [
  { label: "MEERP_OVERVIEW", screens: ["ER00"] },
  { label: "MEERP_GROUP_PROCUREMENT", screens: ["MD01", "PR01", "PR02", "GR01", "GR02"] },
  { label: "MEERP_GROUP_FINANCE", screens: ["FN01", "FN02", "FN03", "FN04", "FN05", "FN06", "FN07"] },
  { label: "MEERP_REPORTS", screens: ["RP01"] },
];

const SCREEN_ICONS: Record<MeERPScreenId, NonNullable<IconProps["name"]>> = {
  ER00: "home", MD01: "medkit", PR01: "shopping cart", PR02: "edit",
  GR01: "truck", GR02: "clipboard check", FN01: "percent", FN02: "chart bar",
  FN03: "calculator", FN04: "credit card", FN05: "book", FN06: "sitemap",
  FN07: "exchange", RP01: "file alternate",
};

const CardMeERP: React.FC<CardMeERPProps> = ({ selectedDivision, apiBaseUrl, apiToken, purPathPrefix, onEvent, MeERP, documentVerificationAccess, receiptWriteAccess }) => {
  const intl = useIntl();
  const [screen, setScreen] = useState<MeERPScreenId>("ER00");
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [procurementTab, setProcurementTab] = useState<ProcurementTab>("materialRequests");
  const [receivingTab, setReceivingTab] = useState<ReceivingTab>("pendingOrders");
  const [reportTab, setReportTab] = useState<"vendor" | "draft" | "templates">("vendor");
  const phaseB = useRef<MeERPPhaseBHandle>(null);
  const [workflowLock, setWorkflowLock] = useState<{ client: PurClientConfig; locked: boolean } | null>(null);
  const [savedTarget, setSavedTarget] = useState<{ client: PurClientConfig; kind: ProcurementTab; recordId: number; revision: number } | null>(null);
  const [savedReceipt, setSavedReceipt] = useState<{ client: PurClientConfig; recordId: number } | null>(null);

  const client = useMemo<PurClientConfig>(() => ({
    apiBaseUrl,
    apiToken,
    divisionId: selectedDivision?.id,
    pathPrefix: purPathPrefix,
  }), [apiBaseUrl, apiToken, purPathPrefix, selectedDivision?.id]);
  const verificationStore = usePurVerificationStore();

  // A non-secret revision remounts all data/drafts even when only auth changes.
  const scope = useRef({ client, revision: 0 });
  if (scope.current.client !== client) scope.current = { client, revision: scope.current.revision + 1 };
  const workflowLocked = workflowLock?.client === client && workflowLock.locked;
  const handleWorkflowLock = useCallback((locked: boolean) => {
    if (scope.current.client === client) setWorkflowLock({ client, locked });
  }, [client]);
  const currentSaved = savedTarget?.client === client && savedTarget.kind === procurementTab ? savedTarget : null;
  const prepareReceipt = (po: PurRecord) => {
    if (scope.current.client !== client) return;
    phaseB.current?.prepareReceipt(po); setScreen("GR02");
  };

  return <section className="card-meerp">
    <header className="card-meerp__header">
      <span className="card-meerp__eyebrow">MeERP</span>
      <h1>{intl.formatMessage({ id: "MEERP_TITLE" })}</h1>
      <p>{intl.formatMessage({ id: "MEERP_DIVISION" }, { name: selectedDivision?.name || intl.formatMessage({ id: "MEERP_NO_DIVISION" }) })}</p>
    </header>
    <div className={navCollapsed ? "card-meerp__layout card-meerp__layout--collapsed" : "card-meerp__layout"}>
      <nav className={navCollapsed ? "card-meerp__nav card-meerp__nav--collapsed" : "card-meerp__nav"} aria-label="MeERP">
        <button
          type="button"
          className="card-meerp__nav-toggle"
          aria-expanded={!navCollapsed}
          aria-label={intl.formatMessage({ id: navCollapsed ? "MEERP_NAV_EXPAND" : "MEERP_NAV_COLLAPSE" })}
          title={intl.formatMessage({ id: navCollapsed ? "MEERP_NAV_EXPAND" : "MEERP_NAV_COLLAPSE" })}
          onClick={() => setNavCollapsed((collapsed) => !collapsed)}
        >{navCollapsed ? "»" : "«"}</button>
        {SCREEN_GROUPS.map((group) => <div className="card-meerp__nav-group" key={group.label}>
          <h2>{intl.formatMessage({ id: group.label })}</h2>
          {group.screens.map((item) => {
            const screenName = intl.formatMessage({ id: `MEERP_SCREEN_${item}` });
            return <button
              type="button"
              key={item}
              disabled={workflowLocked}
              aria-label={screenName}
              aria-pressed={screen === item}
              className={screen === item ? "card-meerp__nav-item card-meerp__nav-item--active" : "card-meerp__nav-item"}
              title={navCollapsed ? screenName : undefined}
              onClick={() => setScreen(item)}
            ><Icon name={SCREEN_ICONS[item]} className="card-meerp__nav-icon" aria-hidden="true" /><span className="card-meerp__nav-label">{screenName}</span></button>;
          })}
        </div>)}
      </nav>
      <div className="card-meerp__main" key={scope.current.revision}>
        {screen === "ER00" && <MeERPOverview onEvent={onEvent} meerp={MeERP} onNavigate={setScreen} />}

        {screen === "PR01" && <>
          <div className="card-meerp__page-heading"><div><h2>{intl.formatMessage({ id: "MEERP_SCREEN_PR01" })}</h2></div><button className="card-meerp__button" type="button" disabled={workflowLocked} onClick={() => { phaseB.current?.openDraft(procurementTab); setScreen("PR02"); }}>{intl.formatMessage({ id: procurementTab === "purchaseOrders" ? "MEERP_DRAFT_OPEN_PURCHASE_ORDER" : "MEERP_DRAFT_OPEN_PURCHASE" })}</button></div>
          <p className="card-meerp__muted">{intl.formatMessage({ id: "MEERP_PROCUREMENT_ACTION_NOTE" })}</p>
          <div className="card-meerp__tabs" role="group" aria-label={intl.formatMessage({ id: "MEERP_SCREEN_PR01" })}>
            <button className="card-meerp__tab" type="button" disabled={workflowLocked} aria-pressed={procurementTab === "materialRequests"} onClick={() => setProcurementTab("materialRequests")}>{intl.formatMessage({ id: "MEERP_MATERIAL_REQUESTS" })}</button>
            <button className="card-meerp__tab" type="button" disabled={workflowLocked} aria-pressed={procurementTab === "purchaseOrders"} onClick={() => setProcurementTab("purchaseOrders")}>{intl.formatMessage({ id: "MEERP_PURCHASE_ORDERS" })}</button>
          </div>
          {procurementTab === "materialRequests" ? (
            <PurResourceList key={`procurement:requests:${currentSaved?.revision || 0}`} onEvent={onEvent} meerp={MeERP} client={client} resource="materialRequests" statuses={["DRAFT", "SUBMITTED", "APPROVED", "PARTIALLY_ORDERED", "ORDERED", "CANCELLED"]}
              enableApproval onWorkflowLockChange={handleWorkflowLock} initialRecordId={currentSaved?.recordId} onPrepareOrder={(draft) => {
                if (scope.current.client !== client) return;
                phaseB.current?.prepareOrderFromRequest(draft); setScreen("PR02");
              }} />
          ) : (
            <PurResourceList key={`procurement:orders:${currentSaved?.revision || 0}`} onEvent={onEvent} meerp={MeERP} client={client} resource="purchaseOrders" statuses={["DRAFT", "PENDING_APPROVAL", "APPROVED", "PARTIALLY_RECEIVED", "TO_BILL", "TO_RECEIVE", "COMPLETED", "CANCELLED"]}
              enableApproval onWorkflowLockChange={handleWorkflowLock} initialRecordId={currentSaved?.recordId} onPrepareReceipt={prepareReceipt} />
          )}
          <button type="button" className="card-meerp__prototype-link" disabled={workflowLocked} onClick={() => setScreen("PR02")}>{intl.formatMessage({ id: "MEERP_SCREEN_PR02" })} →</button>
        </>}

        {screen === "GR01" && <>
          <div className="card-meerp__page-heading"><div><h2>{intl.formatMessage({ id: "MEERP_SCREEN_GR01" })}</h2></div></div>
          <p className="card-meerp__muted">{intl.formatMessage({ id: "MEERP_READ_ONLY" })}</p>
          <div className="card-meerp__tabs" role="group" aria-label={intl.formatMessage({ id: "MEERP_SCREEN_GR01" })}>
            <button className="card-meerp__tab" type="button" aria-pressed={receivingTab === "pendingOrders"} onClick={() => setReceivingTab("pendingOrders")}>{intl.formatMessage({ id: "MEERP_PENDING_ORDERS" })}</button>
            <button className="card-meerp__tab" type="button" aria-pressed={receivingTab === "receipts"} onClick={() => setReceivingTab("receipts")}>{intl.formatMessage({ id: "MEERP_RECEIPT_HISTORY" })}</button>
          </div>
          {receivingTab === "pendingOrders" ? (
            <PurResourceList key="receiving:orders" onEvent={onEvent} meerp={MeERP} client={client} resource="purchaseOrders" initialStatus="APPROVED" statuses={["APPROVED", "PARTIALLY_RECEIVED", "TO_RECEIVE"]} allowAllStatuses={false} onChoose={prepareReceipt} />
          ) : (
            <PurResourceList key={`receiving:receipts:${savedReceipt?.client === client ? savedReceipt.recordId : ""}`} onEvent={onEvent} meerp={MeERP} client={client} resource="receipts" statuses={["DRAFT", "VERIFIED", "CANCELLED"]} initialRecordId={savedReceipt?.client === client ? savedReceipt.recordId : null} onOpenPurchaseOrder={(recordId) => {
              setSavedTarget((previous) => ({ client, kind: "purchaseOrders", recordId, revision: (previous?.revision || 0) + 1 }));
              setProcurementTab("purchaseOrders"); setScreen("PR01");
            }} />
          )}
          <button type="button" className="card-meerp__prototype-link" onClick={() => setScreen("GR02")}>{intl.formatMessage({ id: "MEERP_SCREEN_GR02" })} →</button>
        </>}

        {screen === "RP01" && <>
          <div className="card-meerp__tabs" role="group" aria-label={intl.formatMessage({ id: "MEERP_REPORTS" })}>
            <button className="card-meerp__tab" type="button" aria-pressed={reportTab === "vendor"} onClick={() => setReportTab("vendor")}>{intl.formatMessage({ id: "MEERP_VENDOR_DOCUMENTS" })}</button>
            <button className="card-meerp__tab" type="button" aria-pressed={reportTab === "draft"} onClick={() => setReportTab("draft")}>{intl.formatMessage({ id: "MEERP_DRAFT_DOC_TAB" })}</button>
            <button className="card-meerp__tab" type="button" aria-pressed={reportTab === "templates"} onClick={() => setReportTab("templates")}>{intl.formatMessage({ id: "MEERP_BANK_TEMPLATES" })}</button>
          </div>
          {reportTab === "vendor" ? <>
            <div className="card-meerp__page-heading"><div><h2>{intl.formatMessage({ id: "MEERP_SCREEN_RP01" })}</h2></div></div>
            <PurDocuments onEvent={onEvent} meerp={MeERP} client={client} verificationAccess={documentVerificationAccess} verificationStore={verificationStore} />
          </> : reportTab === "templates" ? <MeERPScreenScaffold screen="RP01" onNavigate={setScreen} /> : null}
        </>}
        <MeERPPhaseB ref={phaseB} client={client} screen={screen} documentDraft={reportTab === "draft"} receiptWriteAccess={receiptWriteAccess} onEvent={onEvent} meerp={MeERP} onOpenReceipt={(record) => {
          setSavedReceipt({ client, recordId: record.id }); setReceivingTab("receipts"); setScreen("GR01");
        }} onInspectReceipt={(recordId) => {
          setSavedReceipt(recordId === null ? null : { client, recordId }); setReceivingTab("receipts"); setScreen("GR01");
        }} onOpenSaved={(kind, record) => {
          setSavedTarget((previous) => ({ client, kind, recordId: record.id, revision: (previous?.revision || 0) + 1 }));
          setProcurementTab(kind); setScreen("PR01");
        }} onOpenPurchaseDraft={() => setScreen("PR02")} />
        {screen.startsWith("FN") && <MeERPScreenScaffold key={screen} screen={screen} onNavigate={setScreen} />}
      </div>
    </div>
  </section>;
};

export default CardMeERP;
